import { useCallback, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import {
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
  Table,
  Td,
  Th,
} from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { api } from '@/lib/api'
import { relativeTime, sentence } from '@/lib/format'
import type { ConversationListItem, Page } from '@/types'

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'awaiting_reply', label: 'Awaiting reply' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'opted_out', label: 'Opted out' },
]

export function ConversationsPage() {
  const [params] = useSearchParams()
  const contactId = params.get('contact_id') ?? undefined
  const [status, setStatus] = useState('')

  const query = useQuery(
    useCallback(
      () =>
        api.get<Page<ConversationListItem>>('/conversations', {
          status: status || undefined,
          contact_id: contactId,
          limit: 100,
        }),
      [status, contactId],
    ),
    [status, contactId],
  )

  return (
    <>
      <PageHeader
        title="Conversations"
        subtitle="Raw WhatsApp history alongside what the agent understood from it."
        action={
          <Select label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        }
      />

      <Card>
        {query.loading && !query.data ? (
          <Spinner />
        ) : query.error ? (
          <ErrorNote message={query.error} onRetry={query.reload} />
        ) : !query.data?.items.length ? (
          <EmptyState
            title="No conversations yet"
            hint="They begin when a contact replies, or when you start outreach from a contact page."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th>Last message</Th>
                <Th numeric>Turns</Th>
                <Th>Window</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((conversation) => (
                <tr key={conversation.id} className="hover:bg-raised">
                  <Td>
                    <Link
                      to={`/conversations/${conversation.id}`}
                      className="flex items-center gap-2.5 hover:underline"
                    >
                      <Avatar name={conversation.contact_name} size={28} />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">
                          {conversation.contact_name}
                        </span>
                        <span className="block text-xs text-ink-faint">
                          {sentence(conversation.goal)}
                        </span>
                      </span>
                    </Link>
                  </Td>
                  <Td>
                    <StatusBadge status={conversation.status} />
                  </Td>
                  <Td className="max-w-sm">
                    <span className="block truncate text-xs text-ink-muted">
                      {conversation.last_message_preview || '—'}
                    </span>
                  </Td>
                  <Td numeric>{conversation.turn_count}</Td>
                  <Td>
                    {/* The 24-hour window decides whether we may write freely at all. */}
                    {conversation.inside_window ? (
                      <Badge tone="good" icon="●">
                        Open
                      </Badge>
                    ) : (
                      <Badge tone="neutral" icon="○">
                        Template only
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <span className="text-xs text-ink-muted">
                      {relativeTime(
                        conversation.last_inbound_at ||
                          conversation.last_outbound_at ||
                          conversation.created_at,
                      )}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}
